/**
 * WriterJourney.jsx — Feb 28 2026, user spec:
 *   "They will live before writer uploads and after and distributed as
 *    achievements which would be published on stats. Coins awarded per
 *    achievement 100 coins for first 10k words, 30k words - 300 coins
 *    then every 20k words 100 coins. Contract consideration at 90k words.
 *    Withdrawal requestion 15 days after contract consideration. Payment
 *    15 days after withdrawal invoice is raised."
 *
 * Sections:
 *   1. Lifetime word counter hero (big number, contract progress bar)
 *   2. Next milestone callout (coin reward + words remaining)
 *   3. Contract status card (under_review / approved / rejected) with ETA
 *   4. Achievements grid — every per-series milestone awarded
 *   5. Withdrawal panel (gated until 15d after contract approval)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown, Trophy, Coins, Lock, CheckCircle2, XCircle,
  Hourglass, ArrowLeft, PenLine, Sparkles, Share2, Copy, Check, Users,
  BarChart3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { authFetch, checkAuth } from '../utils/authUtils';
import JourneyRoadmap from '../components/JourneyRoadmap';
import LibraryQuickDrop from '../components/LibraryQuickDrop';
import WriterStatsPanel from '../components/WriterStatsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COIN_TO_GBP = 0.01;

const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const ContractCard = ({ contract, eligible_withdrawal_at, cooldown_days }) => {
  if (!contract) {
    return (
      <div className="rounded-2xl bg-slate-800/50 border border-slate-700 p-5" data-testid="journey-contract-card">
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-slate-400" />
          <div>
            <h3 className="text-white font-bold text-sm">Contract not yet unlocked</h3>
            <p className="text-xs text-slate-400 mt-0.5">Reach 90,000 lifetime words to begin contract consideration.</p>
          </div>
        </div>
      </div>
    );
  }
  const status = contract.status;
  if (status === 'under_review') {
    return (
      <div className="rounded-2xl bg-amber-900/30 border border-amber-500/30 p-5" data-testid="journey-contract-card">
        <div className="flex items-center gap-3">
          <Hourglass className="w-5 h-5 text-amber-300" />
          <div>
            <h3 className="text-white font-bold text-sm">Contract under review</h3>
            <p className="text-xs text-amber-200 mt-0.5">Triggered on {fmtDate(contract.triggered_at)}. Our team will review and respond soon.</p>
          </div>
        </div>
      </div>
    );
  }
  if (status === 'approved') {
    const now = new Date();
    const eta = eligible_withdrawal_at ? new Date(eligible_withdrawal_at) : null;
    const daysLeft = eta ? Math.max(0, Math.ceil((eta - now) / 86400000)) : null;
    const eligible = daysLeft === 0;
    return (
      <div className="rounded-2xl bg-emerald-900/30 border border-emerald-500/30 p-5" data-testid="journey-contract-card">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-white font-bold text-sm">Contract approved 🎉</h3>
            <p className="text-xs text-emerald-200 mt-0.5">Approved on {fmtDate(contract.approved_at)}.</p>
            {eligible ? (
              <p className="text-xs text-emerald-100 mt-2 font-bold">Withdrawals available — request one below.</p>
            ) : (
              <p className="text-xs text-emerald-200 mt-2">
                Withdrawals open in <span className="font-bold text-white">{daysLeft} day{daysLeft === 1 ? '' : 's'}</span> ({cooldown_days}-day cooldown after approval).
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="rounded-2xl bg-rose-900/30 border border-rose-500/30 p-5" data-testid="journey-contract-card">
        <div className="flex items-start gap-3">
          <XCircle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-white font-bold text-sm">Contract rejected</h3>
            {contract.notes ? <p className="text-xs text-rose-200 mt-1">{contract.notes}</p> : null}
            <p className="text-xs text-rose-200 mt-1">Reach out to support if you'd like to appeal.</p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const WithdrawPanel = ({ journey, balance, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const contract = journey?.contract;
  const eta = journey?.eligible_withdrawal_at ? new Date(journey.eligible_withdrawal_at) : null;
  const eligible = contract?.status === 'approved' && eta && new Date() >= eta;

  const submit = async () => {
    setErr('');
    const amt = parseInt(amount, 10);
    if (!Number.isFinite(amt) || amt <= 0) {
      setErr('Enter a coin amount greater than zero.');
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/writer/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_coins: amt }),
      });
      const d = await r.json();
      if (!r.ok) {
        setErr(d.detail || 'Withdrawal failed.');
        return;
      }
      setAmount('');
      onSuccess && onSuccess(d);
    } catch (e) {
      setErr(e.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-slate-800/50 border border-slate-700 p-5 mt-4" data-testid="journey-withdraw-panel">
      <div className="flex items-center gap-2 mb-3">
        <Coins className="w-5 h-5 text-amber-300" />
        <h3 className="text-white font-bold text-sm">Request a withdrawal</h3>
      </div>
      <p className="text-xs text-slate-400 mb-3">
        Coin balance: <span className="text-amber-200 font-bold tabular-nums">{fmtNum(balance)}</span> coins
        {' '}<span className="text-slate-500">(≈ £{(balance * COIN_TO_GBP).toFixed(2)})</span>
      </p>
      {!eligible && (
        <p className="text-xs text-amber-300 mb-3" data-testid="journey-withdraw-locked">
          Withdrawals open once your contract has been approved + a 15-day cooldown elapses.
        </p>
      )}
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="Amount in coins"
          className="flex-1 px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-white outline-none focus:border-indigo-500"
          disabled={!eligible || busy}
          data-testid="journey-withdraw-amount"
        />
        <Button
          onClick={submit}
          disabled={!eligible || busy}
          className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed"
          data-testid="journey-withdraw-submit"
        >
          {busy ? 'Requesting…' : 'Request'}
        </Button>
      </div>
      {err && (
        <p className="text-xs text-rose-400 mt-2" data-testid="journey-withdraw-err">{err}</p>
      )}
      <p className="text-[11px] text-slate-500 mt-3">
        Payment is wired manually 15 days after your invoice is raised.
      </p>
    </div>
  );
};

// ── Referral / Share-my-code card (Jun 3 2026) ────────────────────────
const ShareCodeCard = ({ codeMeta }) => {
  const [copied, setCopied] = useState(null);
  if (!codeMeta || !codeMeta.unique_code) return null;
  const code = codeMeta.unique_code;
  // Jun 3 2026 follow-up — bake the channel into the URL itself so the
  // admin analytics ledger can later split signups by source. Writers
  // (SY-W-) always emit `utm=author`; pure ambassadors (SY-M-)
  // get `utm=ambassador`. A reader who happens to be both
  // a scribe and an ambassador defaults to the scribe channel since
  // the SY-W- code overrides their SY-M- entry in /codes/mine.
  const utmTag = code.startsWith('SY-W-') ? 'author'
    : code.startsWith('SY-M-') ? 'ambassador'
    : '';
  const qs = `ref=${encodeURIComponent(code)}${utmTag ? `&utm=${utmTag}` : ''}`;
  const inviteUrl = `${window.location.origin}/?${qs}`;
  const sharePayload = {
    title: 'Join me on Smarter You',
    text: `Use my code ${code} to sign up and we both earn coins.`,
    url: inviteUrl,
  };
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1800);
    } catch { /* ignore */ }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share(sharePayload); return; }
      catch { /* user dismissed */ }
    }
    copy(inviteUrl, 'url');
  };
  const linkedSubs = codeMeta.linked_subscribers || 0;
  const earned = codeMeta.recent_events_coin_total || 0;
  return (
    <section
      className="rounded-2xl bg-gradient-to-br from-sky-900/40 via-slate-800/60 to-indigo-900/40 border border-sky-500/30 p-5 sm:p-6 mb-5"
      data-testid="journey-share-code-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-5 h-5 text-sky-300" />
        <h3 className="text-white font-bold text-base sm:text-lg">Share your writer code</h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-emerald-300 inline-flex items-center gap-1">
          <Users className="w-3 h-3" />
          {linkedSubs} subscriber{linkedSubs === 1 ? '' : 's'}
        </span>
      </div>
      <p className="text-[12px] text-slate-300/90 mb-3">
        Every signup with your code earns you 50 coins · 25 more when they hit 30 min of listening · 25 on first finished book · 100 when they upgrade to paid.
        {earned > 0 && (
          <>
            {' '}You've earned{' '}
            <strong className="text-amber-200">{earned.toLocaleString()} coins</strong>
            {' '}from referrals so far.
          </>
        )}
      </p>
      <div className="flex flex-wrap items-stretch gap-2">
        <button
          type="button"
          onClick={() => copy(code, 'code')}
          className="flex-1 min-w-[150px] inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-950/60 border border-sky-400/40 text-sky-100 font-mono font-bold tracking-wider hover:bg-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
          data-testid="journey-copy-code"
          title="Tap to copy the code"
        >
          <span className="truncate">{code}</span>
          {copied === 'code' ? <Check className="w-4 h-4 text-emerald-300 flex-shrink-0" /> : <Copy className="w-4 h-4 text-slate-300 flex-shrink-0" />}
        </button>
        <button
          type="button"
          onClick={() => copy(inviteUrl, 'url')}
          className="flex-1 min-w-[150px] inline-flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg bg-slate-900/50 border border-slate-600 text-slate-200 text-xs hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
          data-testid="journey-copy-invite-url"
          title="Copy the invite link"
        >
          <span className="truncate">{inviteUrl}</span>
          {copied === 'url' ? <Check className="w-4 h-4 text-emerald-300 flex-shrink-0" /> : <Copy className="w-4 h-4 text-slate-300 flex-shrink-0" />}
        </button>
        <button
          type="button"
          onClick={share}
          className="px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white font-bold text-sm inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
          data-testid="journey-share-code"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </div>
    </section>
  );
};

const WriterJourney = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [journey, setJourney] = useState(null);
  const [coinBalance, setCoinBalance] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codeMeta, setCodeMeta] = useState(null);  // Jun 3 2026 — referral code share

  const fetchAll = useCallback(async () => {
    const u = await checkAuth();
    if (!u) { navigate('/login'); return; }
    if (u.role !== 'writer' && u.role !== 'admin') {
      navigate('/profile');
      return;
    }
    setMe(u);
    const [j, ws, iv, cm] = await Promise.allSettled([
      authFetch(`${BACKEND_URL}/api/writer/journey`).then((r) => r.json()),
      authFetch(`${BACKEND_URL}/api/writer/stats`).then((r) => r.json()),
      authFetch(`${BACKEND_URL}/api/writer/invoices`).then((r) => r.json()),
      authFetch(`${BACKEND_URL}/api/codes/mine`).then((r) => r.json()),
    ]);
    if (j.status === 'fulfilled') setJourney(j.value);
    if (ws.status === 'fulfilled') setCoinBalance(ws.value?.coinBalance || 0);
    if (iv.status === 'fulfilled') setInvoices(iv.value?.invoices || []);
    if (cm.status === 'fulfilled') setCodeMeta(cm.value || null);
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-400">Loading your journey…</p>
      </div>
    );
  }

  const lifetime = journey?.lifetime_words || 0;
  const effective = journey?.effective_units ?? lifetime;
  const audioEquiv = journey?.audio_words_equivalent || 0;
  const trigger = journey?.contract_trigger_words || 90000;
  const pct = Math.min(100, (effective / trigger) * 100);
  const next = journey?.next_milestone;
  const achievements = journey?.achievements || [];

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="writer-journey-page">
      {/* Same drag-drop upload widget that lives on /library — single
          "upload channel" reused across writer surfaces per Feb 8 2026
          user spec. Always enabled here since the page itself is only
          reachable by writers/admins. */}
      <LibraryQuickDrop enabled={true} />
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-slate-800" data-testid="journey-back">
          <ArrowLeft className="w-4 h-4 text-slate-300" />
        </button>
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <Crown className="w-7 h-7 text-amber-300" />
          Journey to Top Scribe
        </h1>
        <button
          type="button"
          onClick={() => navigate('/improve')}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-fuchsia-600 to-indigo-600 hover:from-fuchsia-500 hover:to-indigo-500 text-white text-xs font-bold shadow"
          data-testid="journey-open-improve"
          title="Pocket-FM-style per-title analytics"
        >
          <BarChart3 className="w-4 h-4" />
          Improve
        </button>
      </div>

      {/* Hero — lifetime words + contract progress */}
      <section
        className="rounded-2xl bg-gradient-to-br from-purple-900/40 via-slate-800/60 to-amber-900/30 border border-purple-500/30 p-5 sm:p-6 mb-5"
        data-testid="journey-hero"
      >
        <p className="text-[11px] uppercase tracking-widest font-bold text-purple-300">Effective Writing Units</p>
        <h2 className="text-4xl sm:text-5xl font-black text-white mt-1 tabular-nums" data-testid="journey-lifetime-words">
          {fmtNum(effective)}
        </h2>
        <p className="text-[11px] text-slate-400 mt-1">
          {fmtNum(lifetime)} words + {fmtNum(audioEquiv)} audio-equivalent words
          {' '}<span className="text-slate-500">(150 wpm)</span>
        </p>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-300 mb-1.5">
            <span>Contract consideration at {fmtNum(trigger)}</span>
            <span className="tabular-nums font-bold text-amber-200">{Math.floor(pct)}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-900/60 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="journey-progress-bar"
            />
          </div>
        </div>
        {/* Per-source contribution — every writing the author has ever
            published counts here (audiobook minutes · cinematic minutes
            · standalone ebooks/text words). Tapping the Audio or
            Cinematic chip drills into the audio breakdown; tapping the
            Ebook/Text chip opens the words breakdown. Cinematic chip now
            reads minutes (was 0 before because video has no word_count).
            Feb 28 2026 — late evening fix. */}
        {journey?.lifetime_by_source && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="journey-source-split">
            <button
              type="button"
              onClick={() => navigate('/writer-journey/breakdown?type=audio')}
              className="rounded-xl bg-slate-900/50 hover:bg-slate-800 px-2 py-2 transition-colors"
              data-testid="journey-source-audio-btn"
              aria-label="Open audio breakdown"
            >
              <p className="text-[9px] uppercase tracking-widest text-purple-300 font-bold">Audio</p>
              <p className="text-sm font-black text-white tabular-nums">
                {fmtNum(journey?.lifetime_by_source_minutes?.audiobook_minutes ?? 0)}
                <span className="text-[9px] text-slate-400 font-medium ml-1">min</span>
              </p>
            </button>
            <button
              type="button"
              onClick={() => navigate('/writer-journey/breakdown?type=audio')}
              className="rounded-xl bg-slate-900/50 hover:bg-slate-800 px-2 py-2 transition-colors"
              data-testid="journey-source-cinematic-btn"
              aria-label="Open cinematic breakdown"
            >
              <p className="text-[9px] uppercase tracking-widest text-pink-300 font-bold">Cinematic</p>
              <p className="text-sm font-black text-white tabular-nums">
                {fmtNum(journey?.lifetime_by_source_minutes?.cinematic_minutes ?? 0)}
                <span className="text-[9px] text-slate-400 font-medium ml-1">min</span>
              </p>
            </button>
            <button
              type="button"
              onClick={() => navigate('/writer-journey/breakdown?type=words')}
              className="rounded-xl bg-slate-900/50 hover:bg-slate-800 px-2 py-2 transition-colors"
              data-testid="journey-source-text-btn"
              aria-label="Open word count breakdown"
            >
              <p className="text-[9px] uppercase tracking-widest text-amber-300 font-bold">Ebook · Text</p>
              <p className="text-sm font-black text-white tabular-nums">
                {fmtNum(journey.lifetime_by_source.standalone)}
                <span className="text-[9px] text-slate-400 font-medium ml-1">wds</span>
              </p>
            </button>
          </div>
        )}
        {/* Total audio minutes uploaded — clickable, opens breakdown. */}
        {typeof journey?.total_audio_minutes_uploaded === 'number' && journey.total_audio_minutes_uploaded > 0 && (
          <button
            type="button"
            onClick={() => navigate('/writer-journey/breakdown?type=audio')}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-400/30 hover:bg-cyan-500/25 hover:border-cyan-300/50 transition-colors cursor-pointer"
            data-testid="journey-audio-minutes-btn"
            aria-label="Open audio breakdown"
          >
            <span className="text-[10px] uppercase tracking-widest font-bold text-cyan-300">Audio uploaded</span>
            <span className="text-sm font-black text-white tabular-nums">{fmtNum(journey.total_audio_minutes_uploaded)} min</span>
          </button>
        )}
      </section>

      {/* Share-my-code card (Jun 3 2026 user spec) */}
      <ShareCodeCard codeMeta={codeMeta} />

      {/* Scribe Stats panel — Feb 12 2026 user spec: *"Wire stats like
          this and add to journey to top scribe."* Mirrors the Writer
          Stats drawer that Kings/High King see in the user list. The
          panel renders a loading skeleton while `me` is being fetched
          so the section testid is always in the DOM. */}
      <section className="mb-6" data-testid="journey-writer-stats-section">
        <h2 className="text-sm uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-300" /> Scribe Stats 🖋️
        </h2>
        <WriterStatsPanel userId={me?.user_id} />
      </section>

      {/* Next milestone callout */}
      {next && (
        <section
          className="rounded-2xl bg-amber-900/20 border border-amber-500/30 p-4 mb-5 flex items-center gap-3"
          data-testid="journey-next-milestone"
        >
          <Sparkles className="w-5 h-5 text-amber-300 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-white text-sm font-bold">
              Next: <span className="text-amber-200">{fmtNum(next.threshold_words)} words</span>
              {' '}<span className="text-amber-200">· +{next.coins_reward} coins</span>
            </p>
            <p className="text-xs text-amber-200/80">{fmtNum(next.remaining)} words to go on this milestone.</p>
          </div>
        </section>
      )}

      {/* Total coins earned from the journey — surfaced ABOVE the contract
          card per user spec ("Coin total earned should show at the top
          of that analysis before contract under review"). */}
      <section
        className="rounded-2xl bg-gradient-to-r from-amber-900/40 via-yellow-900/30 to-amber-900/40 border border-amber-500/40 p-4 mb-4 flex items-center justify-between gap-3"
        data-testid="journey-coins-earned-card"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-500/20 border border-amber-400/50 flex items-center justify-center flex-shrink-0">
            <Coins className="w-5 h-5 text-amber-200" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold">
              Total Coins Earned from Journey
            </p>
            <p
              className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-tight"
              data-testid="journey-coins-earned-value"
            >
              {fmtNum(journey?.total_coins_from_journey || 0)}
              <span className="text-xs font-medium text-amber-200/80 ml-2">
                ≈ £{((journey?.total_coins_from_journey || 0) * COIN_TO_GBP).toFixed(2)}
              </span>
            </p>
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-[10px] uppercase tracking-widest text-amber-200/60 font-bold">
            Milestones earned
          </p>
          <p className="text-xl font-black text-amber-200 tabular-nums">
            {(journey?.achievements || []).length}
          </p>
        </div>
      </section>

      {/* Contract */}
      <ContractCard
        contract={journey?.contract}
        eligible_withdrawal_at={journey?.eligible_withdrawal_at}
        cooldown_days={journey?.withdrawal_cooldown_days}
      />

      {/* Achievements grid */}
      <section className="mt-6">
        <h3 className="text-sm uppercase tracking-widest font-bold text-slate-400 mb-3 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-300" /> Milestones Earned
          <span className="ml-2 text-[10px] font-bold bg-slate-800 text-slate-300 px-2 py-0.5 rounded-full tabular-nums">
            {achievements.length}
          </span>
        </h3>
        {achievements.length === 0 ? (
          <p className="text-xs text-slate-500" data-testid="journey-achievements-empty">
            No milestones yet — publish your first 10,000 words to unlock the first achievement (+100 coins).
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="journey-achievements-grid">
            {achievements.map((a) => (
              <div
                key={`${a.series_id}:${a.milestone_key}`}
                className="rounded-xl bg-slate-800/60 border border-amber-400/30 p-3"
                data-testid={`journey-achievement-${a.milestone_key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl">🪙</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
                    {a.milestone_key}
                  </span>
                </div>
                <p className="text-white font-bold text-sm mt-2 tabular-nums">+{a.coins_awarded} coins</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {fmtNum(a.threshold_words)} words · {fmtDate(a.awarded_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Withdrawal */}
      <WithdrawPanel journey={journey} balance={coinBalance} onSuccess={fetchAll} />

      {/* Invoices */}
      {invoices.length > 0 && (
        <section className="mt-5">
          <h3 className="text-sm uppercase tracking-widest font-bold text-slate-400 mb-3">
            Your Invoices
          </h3>
          <div className="space-y-2" data-testid="journey-invoices-list">
            {invoices.map((inv) => (
              <div
                key={inv.invoice_id}
                className="flex items-center justify-between p-3 rounded-xl bg-slate-800/60 border border-slate-700"
                data-testid={`journey-invoice-${inv.invoice_id}`}
              >
                <div>
                  <p className="text-white font-bold text-sm tabular-nums">£{Number(inv.amount_currency).toFixed(2)}</p>
                  <p className="text-[11px] text-slate-400">
                    Raised {fmtDate(inv.requested_at)} · ETA {fmtDate(inv.eligible_payment_at)}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${
                  inv.status === 'paid' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'
                }`}>
                  {inv.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer hint */}
      <div className="mt-6 flex items-center gap-2 text-[11px] text-slate-500">
        <PenLine className="w-3.5 h-3.5" />
        <span>Coin rewards: 10K = +100 · 30K = +300 · Every +20K thereafter = +100</span>
      </div>

      {/* Roadmap to Legendary Archon — the tier-by-tier climb. Lives at
          the bottom so the existing milestones list stays the page's
          first impression; scroll down to see the long-haul map. */}
      <section className="mt-10">
        <JourneyRoadmap kind="writer" />
      </section>
    </main>
  );
};

export default WriterJourney;
